import {
  IPanelAssemblyConfig,
  ISectionCaptionOvrd,
  normalizeSectionCaptionOvrd,
  SECTION_CAPTION_OVRD_LANGS,
  SECTION_CAPTION_OVR_VOICE_SLUGS,
} from '../models/assessment-framework';

export function resolveMscatStageCalculatorEnabled(
  stages: IPanelAssemblyConfig['stages'] | undefined,
  stageNumber: number | null | undefined,
): boolean {
  if (stageNumber == null) {
    return true;
  }
  return stages?.[stageNumber]?.isCalculatorEnabled ?? true;
}

/**
 * Ensures all stage numbers from allModules exist in panelAssembly.stages without
 * overwriting existing author selections.
 */
export function syncMscatPanelAssemblyStages(
  panelAssembly: IPanelAssemblyConfig | null | undefined,
): void {
  if (!panelAssembly) {
    return;
  }

  if (!panelAssembly.stages) {
    panelAssembly.stages = {};
  }
  const stageList = panelAssembly.stages;

  const stageNumbers = Array.from(
    new Set(panelAssembly.allModules?.map((m) => m.stageNumber).filter((num) => num != null) ?? []),
  );

  stageNumbers.forEach((num) => {
    if (!stageList[num]) {
      stageList[num] = { isCalculatorEnabled: true };
    }
  });
}

/**
 * The stage's caption override as it should be published: trimmed, blanks dropped, and undefined
 * when nothing is left - so a stage with no real override publishes exactly as it does today.
 * Returns a copy; callers can never mutate the authoring object through the published form.
 */
export function getMscatStageCaptionOvrd(
  stages: IPanelAssemblyConfig['stages'] | undefined,
  stageNumber: number | null | undefined,
): ISectionCaptionOvrd | undefined {
  if (stageNumber == null) {
    return undefined;
  }
  const normalized = normalizeSectionCaptionOvrd(stages?.[stageNumber]?.sectionCaptionOvrd);
  return normalized ? { ...normalized, caption: { ...normalized.caption } } : undefined;
}

/**
 * Sets one language of a stage's caption override in place. Blank text clears that language; when
 * no language is left the whole override is removed (the key is deleted, not left empty). An
 * unknown stage is ignored rather than created - stages come from the modules.
 */
export function setMscatStageCaptionText(
  stages: IPanelAssemblyConfig['stages'] | undefined,
  stageNumber: number,
  lang: string,
  text: string,
): void {
  const stage = stages?.[stageNumber];
  if (!stage) {
    return;
  }
  const trimmed = (text || '').trim();
  if (!trimmed) {
    if (stage.sectionCaptionOvrd?.caption) {
      delete stage.sectionCaptionOvrd.caption[lang];
      if (!Object.keys(stage.sectionCaptionOvrd.caption).some(k => stage.sectionCaptionOvrd.caption[k]?.trim())) {
        delete stage.sectionCaptionOvrd;
      }
    }
    return;
  }
  if (!stage.sectionCaptionOvrd) {
    stage.sectionCaptionOvrd = { caption: {} };
  }
  stage.sectionCaptionOvrd.caption[lang] = trimmed;
}

export function clearMscatStageCaptionOvrd(
  stages: IPanelAssemblyConfig['stages'] | undefined,
  stageNumber: number,
): void {
  if (stages?.[stageNumber]) {
    delete stages[stageNumber].sectionCaptionOvrd;
  }
}

export function getMscatStageVoiceSlug(
  stages: IPanelAssemblyConfig['stages'] | undefined,
  stageNumber: number,
): string {
  return stages?.[stageNumber]?.sectionCaptionOvrd?.voiceSlug || '';
}

/**
 * Sets the voice slug of a stage's caption override. A caption language left blank takes the slug's
 * translation, so the caption students read matches the voice-over (an override without caption
 * text is not published). Clearing the slug keeps the caption text.
 */
export function setMscatStageVoiceSlug(
  stages: IPanelAssemblyConfig['stages'] | undefined,
  stageNumber: number,
  slug: string,
  translate: (slug: string, lang: string) => string,
): void {
  const stage = stages?.[stageNumber];
  if (!stage) {
    return;
  }
  const trimmed = (slug || '').trim();
  if (!trimmed || !SECTION_CAPTION_OVR_VOICE_SLUGS.includes(trimmed)) {
    if (stage.sectionCaptionOvrd) {
      delete stage.sectionCaptionOvrd.voiceSlug;
      if (!normalizeSectionCaptionOvrd(stage.sectionCaptionOvrd)) {
        delete stage.sectionCaptionOvrd;
      }
    }
    return;
  }
  if (!stage.sectionCaptionOvrd) {
    stage.sectionCaptionOvrd = { caption: {} };
  }
  const ovrd = stage.sectionCaptionOvrd;
  ovrd.caption ||= {};
  ovrd.voiceSlug = trimmed;
  SECTION_CAPTION_OVRD_LANGS.forEach(lang => {
    if (ovrd.caption[lang]?.trim()) {
      return;
    }
    const text = (translate(trimmed, lang) || '').trim();
    if (text && text !== trimmed) {
      ovrd.caption[lang] = text;
    }
  });
}
